import React, { Fragment, useCallback, useEffect } from 'react';
import { map, round, slice, values } from 'lodash-es';
import Link from 'next/link';
import { CatalogCompany } from '@/shared/types/catalog';
import { formatCurrency } from '@/shared/utils/utils';
import { getFormattedAvgResponseTime } from '@/shared/utils/sourcing-marketplace-util';
import { CategoryConfig } from '@/shared/types/category';
import { logCommonView } from '@/lib/service';
import { EventSource } from '@/shared/constants/event';
import Image from 'next/image';
import './index.less';
import Rating from '@/components/Common/Rating';
import { SupplierCardStateItem } from '@/shared/constants/sourcing-marketplace';
import { Tooltip } from 'antd';
import { getAvgProductionTime } from '@/shared/types/sourcing-marketplace';
import { CountryFlagEmoji } from '@/shared/constants/country-flag';
import VerifiedSupplierPill from '../../Common/VerifiedSupplierPill';
import LikeIcon from '@/components/Common/Icons/LikeIcon';

interface IProps {
  isMobile: boolean;
  supplier: CatalogCompany;
  category: CategoryConfig;
  isRecommendedSupplier?: boolean;
}

const SupplierCard = ({ supplier, category, isRecommendedSupplier, isMobile }: IProps) => {
  const states = values(SupplierCardStateItem);

  const onClick = () => {
    logCommonView(EventSource.BROWSE_SUPPLIER_SEE_PROFILE, supplier.id);
  };

  const formatProductionCost = useCallback((supplier: CatalogCompany) => {
    const minItemMoq = supplier?.minItemMoqInfo;
    if (minItemMoq && minItemMoq?.qty && minItemMoq?.price) {
      const price = formatCurrency(minItemMoq?.price, true);
      const unitText = minItemMoq?.qty > 1 ? 'units' : 'unit';
      return `${minItemMoq?.qty} ${unitText} at ${price} /unit`;
    } else {
      return 'Inquire';
    }
  }, []);

  const getNotableWorks = (supplier): string =>
    supplier?.notableWorks ? supplier.notableWorks.split(',').join(' / ') : 'Inquire within';

  const getPreviewImages = () => {
    return slice(
      [
        ...map(supplier?.mostPopularImages, 'image'),
        ...map(supplier?.popularItems, (p) => p.images?.[0]),
        ...(supplier.portfolio || []),
      ],
      0,
      4,
    );
  };

  const contactButton = (
    <span className="contact-supplier-btn">
      <Image
        src="https://static.pietrastudio.com/public/file_uploads/bab9a474db800d4ef789e0754345c700.svg"
        width={12}
        height={12}
        alt="chat icon"
      />
      <span data-text="Contact"></span>
    </span>
  );

  const productionTimeStateNode = (
    <div className="stat" key={`ProductionTime-${supplier.id}`}>
      <span className="label">Production time</span>
      <span className="figure">{getAvgProductionTime(supplier)}</span>
    </div>
  );

  const productionCostsStateNode = (
    <div className="stat" key={`ProductionCosts-${supplier.id}`}>
      <span className="label">Production costs</span>
      <span className="figure">{formatProductionCost(supplier)}</span>
    </div>
  );

  const countryFlagStateNode = (
    <div className="stat country-flag" key={`statItemCountryFlag-${supplier.id}`}>
      <span className="label">Location</span>
      <span className="figure">
        {supplier.countryCode && (
          <Tooltip placement="bottom" title={supplier?.country}>
            {CountryFlagEmoji[supplier?.countryCode]}
          </Tooltip>
        )}
        <span>{supplier?.country}</span>
      </span>
    </div>
  );

  const renderStates = (item: SupplierCardStateItem) => {
    switch (item) {
      case SupplierCardStateItem.ProductionTime:
        return productionTimeStateNode;
      case SupplierCardStateItem.ProjectsStartAt:
        return productionCostsStateNode;
      case SupplierCardStateItem.CountryFlag:
        return countryFlagStateNode;
      default:
        return null;
    }
  };

  return (
    <Link className="supplier-card" href={`/sourcing/suppliers/${supplier.handle}`} onClick={onClick}>
      <div className="details">
        <div className="left">
          <div className="header">
            <div className="title-logo">
              {supplier.avatarUrl && (
                <>
                  <Image
                    className="logo"
                    src={supplier.avatarUrl}
                    width={48}
                    height={48}
                    alt={supplier?.name}
                    loading="lazy"
                  />
                </>
              )}
              <div className="title-container">
                <div className="top">
                  <h2 className="title">{supplier?.name}</h2>
                  {!isMobile && isRecommendedSupplier && <div className="recommended-pill">Pietra Recommended</div>}
                </div>
                {supplier?.companyReviewScore > 0 && (
                  <Rating
                    rate={supplier.companyReviewScore / 2}
                    label={`${supplier.companyReviewScore / 2}/5 (${supplier.companyReviewCount}+)`}
                  />
                )}
              </div>
            </div>
          </div>
          {isMobile && isRecommendedSupplier && <div className="recommended-pill">Pietra Recommended</div>}
          {<div className="stats">{states.map((item) => renderStates(item))}</div>}
          {!isMobile && contactButton}
        </div>
        <div className="right">
          {supplier?.capabilities && supplier?.capabilities?.length > 0 && (
            <div className="pills">
              {supplier.capabilities.map((capability) => (
                <VerifiedSupplierPill key={capability} text={capability} icon={<LikeIcon />} />
              ))}
            </div>
          )}
          <div className="images">
            {getPreviewImages()?.map((img, i) => (
              <Fragment key={i}>
                <Image key={i} src={img} width={96} height={96} alt={supplier?.name} loading="lazy" />
              </Fragment>
            ))}
          </div>
          <div className="past-projects">
            <span className="label seo-data-text-before" data-text="Past projects"></span>
            <span className="brand-names">{getNotableWorks(supplier)}</span>
          </div>
        </div>
        {isMobile && contactButton}
      </div>
    </Link>
  );
};

export default SupplierCard;
